import { Mixpanel } from '@/analytics/mixpanel'

type BestBooksPageType = 'genre' | 'person'
type BestBooksCtaType = 'fixed_banner' | 'in_list'
type TrackingRoot = Document | HTMLElement

const PAGE_TYPES = new Set<BestBooksPageType>(['genre', 'person'])
const CTA_TYPES = new Set<BestBooksCtaType>(['fixed_banner', 'in_list'])
const initializedTrackingRoots = new WeakSet<TrackingRoot>()

/** Return a supported tracking value or reject an incomplete CTA hook. */
function trackedValue<T extends string>(
  value: string | undefined,
  supportedValues: Set<T>,
): T | null {
  return value && supportedValues.has(value as T) ? (value as T) : null
}

/** Return a positive integer from a rendered data attribute. */
function positiveInteger(value: string | undefined): number | null {
  if (!value || !/^\d+$/.test(value)) {
    return null
  }
  const integer = Number(value)
  return Number.isSafeInteger(integer) && integer > 0 ? integer : null
}

/**
 * Track clicks from prerendered Best Books CTAs through one delegated handler.
 *
 * The rendered CTA owns all stable event data, so tracking does not infer list
 * position or page type from mutable layout. In-list events require a variant
 * and book position; fixed-banner events report `null` for those fields. The
 * handler never intercepts navigation and initializes each root only once.
 */
export function initializeBestBooksCtaTracking(
  root: TrackingRoot = document,
): void {
  if (initializedTrackingRoots.has(root)) {
    return
  }
  initializedTrackingRoots.add(root)

  root.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
      return
    }

    const cta = event.target.closest<HTMLElement>('[data-best-books-cta-type]')
    if (!cta || !root.contains(cta)) {
      return
    }

    const pageType = trackedValue(cta.dataset.bestBooksCtaPageType, PAGE_TYPES)
    const ctaType = trackedValue(cta.dataset.bestBooksCtaType, CTA_TYPES)
    if (!pageType || !ctaType) {
      return
    }

    let variant: number | null = null
    let bookPosition: number | null = null
    if (ctaType === 'in_list') {
      variant = positiveInteger(cta.dataset.bestBooksCtaVariant)
      bookPosition = positiveInteger(cta.dataset.bestBooksCtaBookPosition)
      if (!variant || variant > 3 || !bookPosition) {
        return
      }
    }

    Mixpanel._track('Best Books CTA Click', {
      page_type: pageType,
      cta_type: ctaType,
      variant,
      book_position: bookPosition,
    })
  })
}
